import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://www.sigplusweb.com/SigWebTablet.js';
const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 120;
const CAPTURE_REFRESH_MS = 50;

let sigWebScriptPromise;

function loadSigWebScript() {
  if (window.IsSigWebInstalled) {
    return Promise.resolve();
  }

  if (sigWebScriptPromise) {
    return sigWebScriptPromise;
  }

  sigWebScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

    if (existingScript) {
      if (window.IsSigWebInstalled) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load Topaz SigWeb.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Topaz SigWeb.'));
    document.body.appendChild(script);
  });

  return sigWebScriptPromise;
}

const TopazSigWebPad = forwardRef(function TopazSigWebPad({ onCleared }, ref) {
  const canvasRef = useRef(null);
  const captureHandleRef = useRef(null);
  const isCapturingRef = useRef(false);

  const [statusMessage, setStatusMessage] = useState('Loading Topaz SigWeb...');
  const [errorMessage, setErrorMessage] = useState('');

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const stopCapture = useCallback(() => {
    if (!isCapturingRef.current || typeof window.SetTabletState !== 'function') {
      return;
    }

    window.SetTabletState(0, captureHandleRef.current);
    captureHandleRef.current = null;
    isCapturingRef.current = false;
  }, []);

  const startCapture = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas || typeof window.SetTabletState !== 'function') {
      return;
    }

    const context = canvas.getContext('2d');

    clearCanvas();
    if (typeof window.SetDisplayXSize === 'function') {
      window.SetDisplayXSize(CANVAS_WIDTH);
    }
    if (typeof window.SetDisplayYSize === 'function') {
      window.SetDisplayYSize(CANVAS_HEIGHT);
    }
    if (typeof window.ClearTablet === 'function') {
      window.ClearTablet();
    }

    captureHandleRef.current = window.SetTabletState(1, context, CAPTURE_REFRESH_MS);
    isCapturingRef.current = true;
  }, [clearCanvas]);

  const initializePad = useCallback(async () => {
    setErrorMessage('');
    setStatusMessage('Loading Topaz SigWeb...');

    try {
      await loadSigWebScript();

      if (typeof window.IsSigWebInstalled !== 'function' || !window.IsSigWebInstalled()) {
        setErrorMessage(
          'Topaz SigWeb is not installed or not responding on this workstation. Install SigWeb, run the certificate installer, and allow Local Network Access in the browser if prompted.'
        );
        setStatusMessage('Signature pad unavailable.');
        return;
      }

      const sigWebVersion = typeof window.GetSigWebVersion === 'function'
        ? window.GetSigWebVersion()
        : '';

      stopCapture();
      startCapture();
      setStatusMessage(
        sigWebVersion
          ? `Signature pad ready (SigWeb ${sigWebVersion}). Ask the customer to sign.`
          : 'Signature pad ready. Ask the customer to sign.'
      );
    } catch (error) {
      setErrorMessage(error.message);
      setStatusMessage('Signature pad unavailable.');
    }
  }, [startCapture, stopCapture]);

  const clearSignature = useCallback(() => {
    if (typeof window.ClearTablet === 'function') {
      window.ClearTablet();
    }

    clearCanvas();
    stopCapture();
    startCapture();

    if (typeof onCleared === 'function') {
      onCleared();
    }
  }, [clearCanvas, onCleared, startCapture, stopCapture]);

  useImperativeHandle(ref, () => ({
    async getSignatureData() {
      if (typeof window.NumberOfTabletPoints !== 'function' || Number(window.NumberOfTabletPoints()) <= 0) {
        throw new Error('Capture a customer signature before completing the service.');
      }

      stopCapture();

      if (typeof window.SetSigCompressionMode === 'function') {
        window.SetSigCompressionMode(1);
      }
      if (typeof window.SetImageXSize === 'function') {
        window.SetImageXSize(CANVAS_WIDTH);
      }
      if (typeof window.SetImageYSize === 'function') {
        window.SetImageYSize(CANVAS_HEIGHT);
      }
      if (typeof window.SetImagePenWidth === 'function') {
        window.SetImagePenWidth(5);
      }

      return new Promise((resolve, reject) => {
        if (typeof window.GetSigImageB64 !== 'function') {
          reject(new Error('Topaz SigWeb image capture is unavailable.'));
          return;
        }

        window.GetSigImageB64((base64Signature) => {
          const sigString = typeof window.GetSigString === 'function'
            ? window.GetSigString()
            : '';

          resolve({
            imageDataUrl: `data:image/png;base64,${base64Signature}`,
            sigString,
          });
        });
      });
    },
    clearSignature,
  }), [clearSignature, stopCapture]);

  useEffect(() => {
    initializePad();

    return () => {
      stopCapture();
    };
  }, [initializePad, stopCapture]);

  return (
    <Box>
      <Typography sx={{ fontSize: '12px' }}>{statusMessage}</Typography>
      {errorMessage && (
        <Alert severity='warning' sx={{ mt: 1 }}>
          {errorMessage}
        </Alert>
      )}
      <Box
        component='canvas'
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        sx={{
          mt: 2,
          width: '100%',
          maxWidth: `${CANVAS_WIDTH}px`,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}
      />
      <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
        <Button variant='outlined' onClick={clearSignature}>
          Clear Signature
        </Button>
        <Button variant='text' onClick={initializePad}>
          Reconnect Pad
        </Button>
      </Stack>
    </Box>
  );
});

export default TopazSigWebPad;
